import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { GrDocumentSound } from "react-icons/gr";
import { MdOutlineFileDownload, MdOutlineFeedback } from "react-icons/md";
import { RiEditLine, RiShareForwardLine } from "react-icons/ri";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AudioViewDialog = ({
  open,
  onClose,
  audioSrc = "",
  audioDownloadId,
  token,
  setAlert,
  onStartRegenerate,
}) => {
  const [alertInfo, setAlertInfo] = useState(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState("");
  const [feedbackConsent, setFeedbackConsent] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [previousFeedbacks, setPreviousFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);

  // Fetch audio with authentication when dialog opens
  useEffect(() => {
    const fetchAudioWithAuth = async () => {
      if (!open || !audioSrc || !token) {
        setAudioBlobUrl(null);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(audioSrc, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setAudioBlobUrl(blobUrl);
        } else {
          console.error("Failed to fetch audio:", response.status);
          setAlertInfo({ severity: "error", message: "Failed to load audio" });
        }
      } catch (error) {
        console.error("Error fetching audio:", error);
        setAlertInfo({ severity: "error", message: "Error loading audio" });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAudioWithAuth();

    // Cleanup blob URL when dialog closes
    return () => {
      if (audioBlobUrl) {
        URL.revokeObjectURL(audioBlobUrl);
      }
    };
  }, [open, audioSrc, token]);



  const handleComingSoon = () => {
    setAlertInfo({ severity: "info", message: "Coming soon" });
  };

  const handleCloseAlert = () => {
    setAlertInfo(null);
  };

  const handleShare = async () => {
    if (!audioDownloadId) {
      setAlertInfo({ severity: "error", message: "Audio not available for sharing" });
      return;
    }

    setSharingInProgress(true);
    setShareDialogOpen(true);
    setShareUrl("");

    try {
      // First, try GET to check if share link already exists
      const getResponse = await fetch(
        `${BASE_URL}/audio/${audioDownloadId}/share`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.share_url) {
          setShareUrl(getData.share_url);
          setSharingInProgress(false);
          return;
        }
      }

      // If no existing link, create one with POST
      const postResponse = await fetch(
        `${BASE_URL}/audio/${audioDownloadId}/share`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ audioId: audioDownloadId }),
        }
      );

      if (postResponse.ok) {
        const postData = await postResponse.json();
        setShareUrl(postData.share_url);
      } else {
        console.error("[AudioViewDialog] Share failed:", postResponse.status);
        setAlertInfo({ severity: "error", message: "Failed to create share link" });
        setShareDialogOpen(false);
      }
    } catch (error) {
      console.error("[AudioViewDialog] Error creating share link:", error);
      setAlertInfo({ severity: "error", message: "Error creating share link" });
      setShareDialogOpen(false);
    } finally {
      setSharingInProgress(false);
    }
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setAlertInfo({ severity: "success", message: "Link copied to clipboard!" });
    } catch (error) {
      console.error("[AudioViewDialog] Error copying to clipboard:", error);
      setAlertInfo({ severity: "error", message: "Failed to copy link" });
    }
  };

  // Fetch previous feedbacks without opening dialog
  const fetchPreviousFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const response = await fetch(
        `${BASE_URL}/project_feedbacks?id=${audioDownloadId}&type=audio&version=1`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPreviousFeedbacks(data.feedbacks || []);
      } else {
        console.error("[AudioViewDialog] Failed to fetch feedbacks:", response.status);
        setPreviousFeedbacks([]);
      }
    } catch (error) {
      console.error("[AudioViewDialog] Error fetching feedbacks:", error);
      setPreviousFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleOpenFeedback = () => {
    setFeedbackRating(0);
    setFeedbackComments("");
    setFeedbackConsent(false);
    setFeedbackDialogOpen(true);
    // Fetch previous feedbacks
    fetchPreviousFeedbacks();
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      setAlertInfo({ severity: "warning", message: "Please select a rating" });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const formData = new FormData();
      formData.append("stars", feedbackRating);
      formData.append("comments", feedbackComments);
      formData.append("id", audioDownloadId);
      formData.append("type", "audio");
      formData.append("version", 1);

      const response = await fetch(`${BASE_URL}/submit_feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setAlertInfo({ severity: "success", message: "Feedback submitted successfully!" });
        setFeedbackDialogOpen(false);
        // Reset form fields
        setFeedbackRating(0);
        setFeedbackComments("");
        setFeedbackConsent(false);
      } else {
        console.error("[AudioViewDialog] Feedback submission failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to submit feedback" });
      }
    } catch (error) {
      console.error("[AudioViewDialog] Error submitting feedback:", error);
      setAlertInfo({ severity: "error", message: "Error submitting feedback" });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ratingOptions = [
    { value: 1, label: "Awful", emoji: "😣" },
    { value: 2, label: "Poor", emoji: "😞" },
    { value: 3, label: "Neutral", emoji: "😐" },
    { value: 4, label: "Good", emoji: "🤩" },
    { value: 5, label: "Excellent", emoji: "😍" },
  ];

  // Handle download with authentication
  const handleDownload = async () => {
    if (!audioDownloadId || !token) {
      if (setAlert) {
        setAlert({ severity: "error", message: "Cannot download audio" });
      } else {
        setAlertInfo({ severity: "error", message: "Cannot download audio" });
      }
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(`${BASE_URL}/audio/download/${audioDownloadId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Pubvision_audioSummary_${audioDownloadId}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        if (setAlert) {
          setAlert({ severity: "success", message: "Download started" });
        }
      } else {
        console.error("Failed to download audio:", response.status);
        if (setAlert) {
          setAlert({ severity: "error", message: "Failed to download audio" });
        } else {
          setAlertInfo({ severity: "error", message: "Failed to download audio" });
        }
      }
    } catch (error) {
      console.error("Error downloading audio:", error);
      if (setAlert) {
        setAlert({ severity: "error", message: "Error downloading audio" });
      } else {
        setAlertInfo({ severity: "error", message: "Error downloading audio" });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: "900px",
            maxWidth: "900px",
            borderRadius: "8px",
            overflow: "visible",
            fontFamily: "'Inter', sans-serif",
          },
        }}
        sx={{
          "& *": { fontFamily: "'Inter', sans-serif !important" },
        }}
      >
        <IconButton
          style={{ position: "absolute", top: 8, right: 8, color: "#838383" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle style={{ padding: "16px", borderBottom: "1px solid #eee" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <GrDocumentSound style={{ color: "#1E4DE3", fontSize: "20px" }} />

            </div>



            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "8px",
                marginRight: "48px",
              }}
            >
              <Tooltip title="Coming soon">
                <Button
                  variant="contained"
                  startIcon={<RiEditLine style={{ color: "#000" }} />}
                  onClick={handleComingSoon}
                  style={{
                    backgroundColor: "#d3d3d3",
                    color: "#000",
                    textTransform: "none",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Edit
                </Button>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={isDownloading ? <CircularProgress size={16} sx={{ color: "#1E4DE3" }} /> : <MdOutlineFileDownload style={{ color: "#1E4DE3" }} />}
                onClick={handleDownload}
                disabled={isDownloading || !audioDownloadId}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                  },
                }}
              >
                {isDownloading ? "Downloading..." : "Download"}
              </Button>

              <Button
                variant="outlined"
                startIcon={sharingInProgress ? <CircularProgress size={16} sx={{ color: "#1E4DE3" }} /> : <RiShareForwardLine style={{ color: "#1E4DE3" }} />}
                onClick={handleShare}
                disabled={sharingInProgress || !audioDownloadId}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#d3d3d3",
                    color: "#1E4DE3",
                  },
                }}
              >
                Share
              </Button>

              <Button
                variant="outlined"
                startIcon={<MdOutlineFeedback style={{ color: "#1E4DE3" }} />}
                onClick={handleOpenFeedback}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Feedback
              </Button>
            </div>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: "16px" }}>
          <div
            style={{
              position: "relative",
              width: "95%",
              backgroundColor: "#f0f0f0",
              marginBottom: "16px",
              padding: "20px",
              borderRadius: "4px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isLoading ? (
              <CircularProgress size={40} />
            ) : audioBlobUrl ? (
              <audio
                src={audioBlobUrl}
                controls
                style={{
                  width: "100%",
                  outline: "none",
                }}
              >
                <track
                  kind="captions"
                  src="captions.vtt"
                  srcLang="en"
                  label="English"
                />
                Your browser does not support the audio element.
              </audio>
            ) : (
              <p style={{ color: "#666" }}>No audio available</p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<GrDocumentSound style={{ color: "#fff" }} />}
              onClick={handleComingSoon}
              style={{
                backgroundColor: "#1E4DE3",
                color: "#fff",
                textTransform: "none",
                boxShadow: "none",
                cursor: "pointer",
              }}
            >
              Regenerate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snackbar for alerts - displayed outside the dialog */}
      <Snackbar
        open={!!alertInfo}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertInfo?.severity || "info"}
          sx={{ width: '100%' }}
        >
          {alertInfo?.message}
        </Alert>
      </Snackbar>

      {/* Share Link Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "8px",
              minWidth: 400,
              backgroundColor: "#2d2d2d",
              color: "#fff",
            },
          },
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          color: "#fff"
        }}>
          <span style={{ fontSize: "16px" }}>Link to 'Pubvision Audio' copied</span>
          <IconButton
            onClick={() => setShareDialogOpen(false)}
            sx={{ color: "#fff", padding: "4px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 16px 16px 16px" }}>
          {sharingInProgress ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}>
              <CircularProgress size={24} sx={{ color: "#fff" }} />
              <span style={{ marginLeft: "12px", color: "#fff" }}>Loading share link...</span>
            </div>
          ) : (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#3d3d3d",
              borderRadius: "4px",
              padding: "8px 12px",
            }}>
              <input
                type="text"
                value={shareUrl}
                readOnly
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "14px",
                  outline: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              <Button
                variant="contained"
                onClick={handleCopyShareUrl}
                startIcon={<ContentCopyIcon />}
                sx={{
                  backgroundColor: "#4a4a4a",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#5a5a5a",
                  },
                }}
              >
                Copy
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "16px",
              minWidth: 400,
              maxWidth: 450,
              maxHeight: "90vh",
            },
          },
        }}
      >
        <DialogTitle sx={{
          padding: "0 0 16px 0",
          fontWeight: 600,
          fontSize: "18px",
        }}>
          Help us improve our product for you
        </DialogTitle>
        <DialogContent sx={{ padding: 0, maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}>
          {loadingFeedbacks ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={24} sx={{ color: "#1E4DE3" }} />
            </Box>
          ) : (
            <>
              {/* Previous Feedbacks Section */}
              {previousFeedbacks && previousFeedbacks.length > 0 && (
                <Box sx={{ marginBottom: "24px" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", marginBottom: "12px", color: "#333" }}>
                    Previous Reviews
                  </Typography>
                  <Box sx={{ maxHeight: "300px", overflowY: "auto", paddingRight: "8px" }}>
                    {previousFeedbacks.map((feedback) => {
                      const ratingOption = ratingOptions.find(opt => opt.value === feedback.stars);
                      const feedbackDate = new Date(feedback.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      });
                      
                      return (
                        <Box
                          key={feedback.feedback_id}
                          sx={{
                            padding: "12px",
                            marginBottom: "12px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                            <span style={{ fontSize: "24px", marginRight: "8px" }}>
                              {ratingOption ? ratingOption.emoji : "😐"}
                            </span>
                            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#666" }}>
                              {ratingOption ? ratingOption.label : `${feedback.stars} stars`}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: "#999", marginLeft: "auto" }}>
                              {feedbackDate}
                            </Typography>
                          </Box>
                          {feedback.comments && (
                            <Typography sx={{ fontSize: "13px", color: "#555", lineHeight: 1.5 }}>
                              {feedback.comments}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", marginTop: "20px", marginBottom: "12px", color: "#333" }}>
                    Add New Review
                  </Typography>
                </Box>
              )}

              {/* New Feedback Form */}
              <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
                {previousFeedbacks && previousFeedbacks.length > 0 
                  ? "How would you like to rate this version?"
                  : "How would you like to describe your experience with the app today?"}
              </p>

              {/* Rating Emojis */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}>
                {ratingOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => setFeedbackRating(option.value)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: feedbackRating === option.value ? "#e8f0fe" : "transparent",
                      border: feedbackRating === option.value ? "2px solid #1E4DE3" : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: "32px" }}>{option.emoji}</span>
                    <span style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>{option.label}</span>
                  </div>
                ))}
              </div>

              {/* Comments TextField */}
              <TextField
                multiline
                rows={4}
                placeholder="Tell us more about your experience (optional)"
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value.slice(0, 500))}
                fullWidth
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                }}
              />
              <p style={{ margin: "0 0 16px 0", color: "#999", fontSize: "12px" }}>
                {500 - feedbackComments.length}/500 characters left
              </p>

              {/* Consent Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={feedbackConsent}
                    onChange={(e) => setFeedbackConsent(e.target.checked)}
                    sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                  />
                }
                label={
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    I consent to PubVision AI contacting me based on my feedback
                  </span>
                }
              />

              {/* Buttons */}
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "20px",
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setFeedbackDialogOpen(false)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#ccc",
                    color: "#333",
                    "&:hover": {
                      borderColor: "#999",
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#1E4DE3",
                    "&:hover": {
                      backgroundColor: "#1a3fb8",
                    },
                  }}
                >
                  {submittingFeedback ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit feedback"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
AudioViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  audioSrc: PropTypes.string,
  audioDownloadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
  setAlert: PropTypes.func,
  onStartRegenerate: PropTypes.func,
};

export default AudioViewDialog;
